import { useState, useEffect, useMemo } from 'react';
import { PartyPopper, Play, Square, X, Minus, Plus, Trophy, Shuffle, Hand, Users, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import type { Event } from '../../services/events';
import { subscribeToDJPrizes, type Prize, type PrizeType } from '../../services/prizes';
import {
  MAX_DANCE_OFF_CONTESTANTS,
  MIN_DANCE_OFF_CONTESTANTS,
  cancelDanceOff,
  closeDanceOff,
  createDanceOff,
  danceOffClaimUrl,
  subscribeToDJDanceOffs,
  subscribeToDanceOffClaim,
  subscribeToDanceOffVotes,
  type DanceOff,
  type DanceOffClaim,
  type DanceOffVote,
  type DanceOffWinner,
} from '../../services/danceOff';
import { findLeaders, isDanceOffResultCurrent, resolveWinners, tallyDanceOffVotes, winnersLabel, type DanceOffTieBreak } from '../../utils/danceOffTally';
import { cryptoRandomIndex } from '../../utils/rouletteDraw';
import { PRIZE_OPTIONS, prizeNeedsText } from './prizeOptions';

interface DanceOffPanelProps {
  eventId: string;
  djId: string;
  event: Event;
}

type ClosePrompt = { kind: 'tie'; leaders: number[] } | { kind: 'no-votes' } | null;

function ClaimQr({ eventUrl, winner, prize }: { eventUrl: string; winner: DanceOffWinner; prize: Prize | undefined }) {
  const [claim, setClaim] = useState<DanceOffClaim | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!winner.claimId) return;
    const unsubscribe = subscribeToDanceOffClaim(winner.claimId, (next) => {
      setClaim(next);
      setLoading(false);
    }, () => toast.error("Couldn't load the claim code"));
    return () => unsubscribe();
  }, [winner.claimId]);

  const claimed = prize && prize.status !== 'configured';
  if (claimed) {
    return (
      <div className="flex items-center gap-2 px-3 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
        <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
          Contestant {winner.contestant} — ✓ Claimed by {prize.winnerNickname || 'an attendee'}
        </span>
      </div>
    );
  }

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading claim code…</p>;
  }

  if (!claim) {
    return <p className="text-sm text-red-600 dark:text-red-400">Claim code for contestant {winner.contestant} is missing.</p>;
  }

  return (
    <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-white border border-gray-200 dark:border-gray-700">
      <QRCodeSVG value={danceOffClaimUrl(eventUrl, claim.id, claim.token)} size={240} marginSize={2} level="M" />
      <p className="text-sm font-semibold text-gray-900 text-center">
        Contestant {winner.contestant} — scan to claim your prize
      </p>
    </div>
  );
}

function VoteBars({ counts, totalVotes }: { counts: number[]; totalVotes: number }) {
  const max = Math.max(1, ...counts);
  return (
    <div className="space-y-2">
      {counts.map((count, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="w-6 text-right text-sm font-bold text-gray-900 dark:text-white">{index + 1}</span>
          <div className="flex-1 h-6 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
            <div className="h-full bg-pink-500 transition-all" style={{ width: `${(count / max) * 100}%` }} />
          </div>
          <span className="w-8 text-right text-sm tabular-nums text-gray-700 dark:text-gray-300">{count}</span>
        </div>
      ))}
      <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
        <Users className="w-3 h-3" />
        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
      </p>
    </div>
  );
}

export default function DanceOffPanel({ eventId, djId, event }: DanceOffPanelProps) {
  const [danceOffs, setDanceOffs] = useState<DanceOff[]>([]);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [votes, setVotes] = useState<DanceOffVote[]>([]);
  const [startingNew, setStartingNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closePrompt, setClosePrompt] = useState<ClosePrompt>(null);

  const [title, setTitle] = useState('Dance-off');
  const [contestantCount, setContestantCount] = useState(MIN_DANCE_OFF_CONTESTANTS);
  const [prizeType, setPrizeType] = useState<PrizeType>('shoutout');
  const [customText, setCustomText] = useState('');

  useEffect(() => {
    const unsubscribe = subscribeToDJDanceOffs(eventId, setDanceOffs, () => toast.error("Couldn't load dance-offs"));
    return () => unsubscribe();
  }, [eventId]);

  useEffect(() => {
    const unsubscribe = subscribeToDJPrizes(eventId, setPrizes);
    return () => unsubscribe();
  }, [eventId]);

  const latest = danceOffs[0] ?? null;
  const openDanceOff = latest?.status === 'open' ? latest : null;
  const closedDanceOff = latest?.status === 'closed' && !startingNew && isDanceOffResultCurrent(latest.closedAt, Date.now())
    ? latest
    : null;

  useEffect(() => {
    if (!openDanceOff) {
      setVotes([]);
      return;
    }
    const unsubscribe = subscribeToDanceOffVotes(eventId, openDanceOff.id, setVotes, () => toast.error("Couldn't load live votes"));
    return () => unsubscribe();
  }, [eventId, openDanceOff?.id]);

  const tally = useMemo(
    () => (openDanceOff ? tallyDanceOffVotes(votes, openDanceOff.contestantCount) : null),
    [openDanceOff, votes],
  );

  const prizesById = useMemo(() => new Map(prizes.map((prize) => [prize.id, prize])), [prizes]);

  const handleStart = async () => {
    setBusy(true);
    try {
      const trimmed = customText.trim();
      await createDanceOff(eventId, djId, {
        title,
        contestantCount,
        prizeType,
        prizeCustomText: prizeNeedsText(prizeType) && trimmed ? trimmed : null,
      });
      setStartingNew(false);
      toast.success('Dance-off voting is open');
    } catch (error) {
      console.error('[DanceOff] Start failed:', error);
      toast.error("Couldn't start the dance-off");
    } finally {
      setBusy(false);
    }
  };

  const finishClose = async (winners: number[], tieBreak: DanceOffTieBreak) => {
    if (!openDanceOff) return;
    setBusy(true);
    try {
      await closeDanceOff(openDanceOff, votes, winners, tieBreak);
      setClosePrompt(null);
      toast.success(winners.length ? `${winnersLabel(winners)} wins!` : 'Dance-off closed with no winner');
    } catch (error) {
      console.error('[DanceOff] Close failed:', error);
      toast.error("Couldn't close the dance-off");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (!tally) return;
    const leaders = findLeaders(tally.counts);
    if (leaders.length === 0) {
      setClosePrompt({ kind: 'no-votes' });
    } else if (leaders.length === 1) {
      void finishClose(leaders, 'none');
    } else {
      setClosePrompt({ kind: 'tie', leaders });
    }
  };

  const handleTieBreak = (leaders: number[], tieBreak: DanceOffTieBreak, djPick?: number) => {
    const winners = resolveWinners(leaders, tieBreak, { djPick, randomIndex: cryptoRandomIndex });
    void finishClose(winners, tieBreak);
  };

  const handleCancel = async () => {
    if (!openDanceOff) return;
    setBusy(true);
    try {
      await cancelDanceOff(openDanceOff.id);
      setClosePrompt(null);
      toast.success('Dance-off cancelled');
    } catch (error) {
      console.error('[DanceOff] Cancel failed:', error);
      toast.error("Couldn't cancel the dance-off");
    } finally {
      setBusy(false);
    }
  };

  const history = danceOffs.filter((danceOff) => danceOff.status === 'closed');

  return (
    <div className="space-y-4">
      {openDanceOff && tally && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded-lg">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-pink-500" />
            </span>
            <span className="text-sm font-medium text-pink-700 dark:text-pink-300">{openDanceOff.title} — voting open</span>
          </div>

          <VoteBars counts={tally.counts} totalVotes={tally.totalVotes} />

          {closePrompt?.kind === 'tie' && (
            <div className="space-y-2 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                It's a tie between {winnersLabel(closePrompt.leaders).replace('Contestants ', 'contestants ')}.
              </p>
              <button
                onClick={() => handleTieBreak(closePrompt.leaders, 'random')}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <Shuffle className="w-4 h-4" /> Pick randomly
              </button>
              <div className="space-y-1">
                <p className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400"><Hand className="w-3 h-3" /> I'll pick:</p>
                <div className="flex flex-wrap gap-2">
                  {closePrompt.leaders.map((leader) => (
                    <button
                      key={leader}
                      onClick={() => handleTieBreak(closePrompt.leaders, 'dj_picks', leader)}
                      disabled={busy}
                      className="min-w-[3rem] px-3 py-2 text-sm font-bold rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                    >
                      {leader}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => handleTieBreak(closePrompt.leaders, 'all_win')}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <Trophy className="w-4 h-4" /> Everyone tied wins
              </button>
              <button onClick={() => setClosePrompt(null)} className="w-full text-xs text-gray-500 dark:text-gray-400 underline">
                Keep voting open
              </button>
            </div>
          )}

          {closePrompt?.kind === 'no-votes' && (
            <div className="space-y-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-sm text-gray-800 dark:text-gray-200">No votes — close without a winner?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => finishClose([], 'none')}
                  disabled={busy}
                  className="flex-1 px-3 py-2 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
                >
                  Close without a winner
                </button>
                <button
                  onClick={() => setClosePrompt(null)}
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Keep voting open
                </button>
              </div>
            </div>
          )}

          {!closePrompt && (
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                disabled={busy}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 font-medium"
              >
                <Square className="w-4 h-4" />
                {busy ? 'Closing…' : 'Close Voting'}
              </button>
              <button
                onClick={handleCancel}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {!openDanceOff && closedDanceOff && (
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{closedDanceOff.title}</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {closedDanceOff.winners.length
                ? `🏆 ${winnersLabel(closedDanceOff.winners.map((winner) => winner.contestant))} ${closedDanceOff.winners.length === 1 ? 'wins' : 'win'}!`
                : 'No votes — no winner'}
            </p>
          </div>
          {closedDanceOff.winners.map((winner) => (
            <ClaimQr
              key={winner.contestant}
              eventUrl={event.eventUrl || eventId}
              winner={winner}
              prize={winner.prizeId ? prizesById.get(winner.prizeId) : undefined}
            />
          ))}
          <button
            onClick={() => setStartingNew(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium"
          >
            <PartyPopper className="w-4 h-4" />
            New Dance-off
          </button>
        </div>
      )}

      {!openDanceOff && !closedDanceOff && (
        <div className="space-y-3">
          <div>
            <label htmlFor="dance-off-title" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Title</label>
            <input
              id="dance-off-title"
              type="text"
              value={title}
              maxLength={60}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Contestants</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setContestantCount((count) => Math.max(MIN_DANCE_OFF_CONTESTANTS, count - 1))}
                disabled={contestantCount <= MIN_DANCE_OFF_CONTESTANTS}
                aria-label="Fewer contestants"
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center text-xl font-bold text-gray-900 dark:text-white tabular-nums">{contestantCount}</span>
              <button
                onClick={() => setContestantCount((count) => Math.min(MAX_DANCE_OFF_CONTESTANTS, count + 1))}
                disabled={contestantCount >= MAX_DANCE_OFF_CONTESTANTS}
                aria-label="More contestants"
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Prize</label>
            <div className="grid grid-cols-2 gap-2">
              {PRIZE_OPTIONS.map((option) => (
                <button
                  key={option.type}
                  onClick={() => setPrizeType(option.type)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border ${
                    prizeType === option.type
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <span>{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {prizeNeedsText(prizeType) && (
            <div>
              <label htmlFor="dance-off-prize-text" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                {prizeType === 'shoutout' ? 'Shoutout message' : 'Prize description'}
              </label>
              <input
                id="dance-off-prize-text"
                type="text"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                placeholder={prizeType === 'shoutout' ? 'e.g., "Best moves of the night!"' : 'e.g., "Free drink at the bar"'}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
          )}

          <button
            onClick={handleStart}
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 font-medium"
          >
            <Play className="w-4 h-4" />
            {busy ? 'Starting…' : 'Start Dance-off'}
          </button>
        </div>
      )}

      {history.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            History ({history.length})
          </h3>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {history.map((danceOff) => (
              <div key={danceOff.id} className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{danceOff.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {winnersLabel(danceOff.winners.map((winner) => winner.contestant))} · {danceOff.results?.totalVotes ?? 0} votes
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                  {new Date(danceOff.closedAt ?? danceOff.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
